import { Injectable } from "@angular/core";
import { EMPTY, Observable, Subject } from "rxjs";
import { GenericService, LoaderConfig } from "./generic.service";
import { environment } from "src/environments/environment";
import { ServerResponse } from "../models/ServerResponse";
import { DialogRef, FilterOperator, ISAPI, ISDialogService, ISPagination, MVFilter, MVListingConfigs } from "is-lib";
// Aliased: models/Contract also exports an IContract (the contracts list item)
import { IContract as IContractForm } from "../models/ContractResponse";
import { UsersService } from "./users.service";
import { ca, tr } from "intl-tel-input/i18n";
import { HttpParams } from "@angular/common/http";
import { ContractAccessRequest } from "../models/ContractAccessRequest";
import { IContract, IContractDetails } from "../models/Contract";
import { IContractElectricitySupply } from "../models/ElectricitySupply";
import { IContractNaturalGasSupply } from "../models/NaturalGasSupply";
import { IContractSelfConsumptionSupply } from "../models/SelfConsumptionSupply";
import { ElectricitySupplyContractEconomicCondition, ElectricitySupplyContracts } from "../models/ElectricitySupplyContracts";
import { ICreateContractElectricitySupplyProposal, ICreateContractEnergySupplyProposal } from "../models/EnergySupplyProposals";

@Injectable({
    providedIn: `root`
})

export class ContractsService{

    constructor(private _httpService: GenericService, private _user: UsersService, private _isDialog: ISDialogService) { }

    getContractAccessRequests(body?: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>> {
        if (!body) {
            body = new MVListingConfigs([], null, new ISPagination(999, 0));
        }
        return this._httpService.post(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/ContractAccessRequests/Get`, body, undefined, loaderConfig);
    }

    getContract(contractId: string, loaderConfig: LoaderConfig | boolean = false){
        return this._httpService.get<IContractForm>(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}`, undefined, loaderConfig);
    }

    getContractFiles(contractId: string, body?: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>> {
        if (!body) {
            body = new MVListingConfigs([], null, new ISPagination(999, 0));
        }
        return this._httpService.post(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}/Files/Get`, body, undefined, loaderConfig);
    }

    getContractHistory(contractId: string, body?: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>> {
        if (!body) {
            body = new MVListingConfigs([], null, new ISPagination(999, 0));
        }
        return this._httpService.post(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}/History/Get`, body, undefined, loaderConfig);
    }

    getContracts(body: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<IContract[]>> {
        return this._httpService.post(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/Get`, body, undefined, loaderConfig);
    }

    getContractElectricitySupplies(contractId: string, body?: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<IContractElectricitySupply[]>> {
        if (!body) {
            body = new MVListingConfigs([], null, new ISPagination(999, 0));
        }
        return this._httpService.post(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}/ElectricitySupplies/Get`, body, undefined, loaderConfig);
    }

    getContractElectricitySuppliesId(electricitySupplyId: string, contractId: string, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>> {
        return this._httpService.get(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}/ElectricitySupplies/${electricitySupplyId}`, undefined, loaderConfig);
    }

    getContractElectricitySuppliesHistory(electricitySupplyId: string, contractId: string, body?: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>> {
        if (!body) {
            body = new MVListingConfigs([], null, new ISPagination(999, 0));
        }
        return this._httpService.post(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}/ElectricitySupplies/${electricitySupplyId}/History/Get`, body, undefined, loaderConfig);
    }

    getContractNaturalGasSupplies(contractId: string, body?: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<IContractNaturalGasSupply[]>> {
        if (!body) {
            body = new MVListingConfigs([], null, new ISPagination(999, 0));
        }
        return this._httpService.post(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}/NaturalGasSupplies/Get`, body, undefined, loaderConfig);
    }

    getContractNaturalGasSuppliesId(gasSupplyId: string, contractId: string, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>> {
        return this._httpService.get(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}/NaturalGasSupplies/${gasSupplyId}`, undefined, loaderConfig);
    }

    getContractNaturalGasSuppliesHistory(naturalGasSupplyId: string, contractId: string, body?: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>> {
        if (!body) {
            body = new MVListingConfigs([], null, new ISPagination(999, 0));
        }
        return this._httpService.post(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}/NaturalGasSupplies/${naturalGasSupplyId}/History/Get`, body, undefined, loaderConfig);
    }

    getContractSelfConsumptionSupplies(contractId: string, body?: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<IContractSelfConsumptionSupply[]>> {
        if (!body) {
            body = new MVListingConfigs([], null, new ISPagination(999, 0));
        }
        return this._httpService.post(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}/SelfConsumptionContracts/Get`, body, undefined, loaderConfig);
    }
    
    getContractSelfConsumptionSuppliesId(selfConsumptionSupplyId: string, contractId: string, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>> {
        return this._httpService.get(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}/SelfConsumptionContracts/${selfConsumptionSupplyId}`, undefined, loaderConfig);
    }

    getContractSelfConsumptionSuppliesHistory(selfConsumptionSupplyId: string, contractId: string, body?: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>> {
        if (!body) {
            body = new MVListingConfigs([], null, new ISPagination(999, 0));
        }
        return this._httpService.post(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}/SelfConsumptionContracts/${selfConsumptionSupplyId}/History/Get`, body, undefined, loaderConfig);
    }

    getContractDetails(contractGuid: string, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<IContractDetails>> {
        return this._httpService.get<IContractDetails>(`${environment.apiUrl}Contracts/${this._user.userCountryCode}/${contractGuid}`, loaderConfig);
    }

    getContractsStatuses(loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>>{
        return this._httpService.get(`${environment.apiUrl}Contracts/${this._user.userCountryCode}/Statuses`, undefined, loaderConfig);
    }

    getContractTypesByEntityTypeDescription(entityType: string, loaderConfig: LoaderConfig | boolean = false) {
        let params = new HttpParams().set('entityType', entityType);

        return this._httpService.get(`${environment.apiUrl}Contracts/${this._user.userCountryCode}/TypesByEntityType`, loaderConfig, params)
    }

    getContractContactPeople(contractId: string, body?: ISAPI, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>> {
        if (!body) {
            body = new ISAPI([], new ISPagination(999, 0));
        }
        return this._httpService.post(`${environment.apiUrl}Contracts/${this._user.userData?.countryCode}/${contractId}/ContactPeople/Get`, body, undefined, loaderConfig);
    }

    getContractHistoryComparison(contractId: string, historyId: string, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>>{
        return this._httpService.get(`${environment.apiUrl}Contracts/${this._user.userCountryCode}/${contractId}/History/Status/${historyId}/Comparison`, loaderConfig);
    }

    getContractAccessRequest(body: ContractAccessRequest, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>> {
        // if (!body) {
        //     body = new ISAPI([], new ISPagination(999, 0));
        // }
        return this._httpService.post(`${environment.apiUrl}ContractAccesses/${this._user.userCountryCode}/Mine/ContractAccessRequest`, body, undefined, loaderConfig);
    }

    getElectricitySupplyContractsOfContract(contractId: string, body?: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<ElectricitySupplyContracts[]>> {
        if (String.isNullOrUndefinedOrWhitespace(contractId)) {
            return EMPTY;
        }
        
        if (!body) {
            body = new MVListingConfigs([],
            null,
            new ISPagination(999, 0));
        }
        
        body.filters.push(new MVFilter('FkMVContract', contractId, 'id', FilterOperator.equals));

        return this._httpService.post(`${environment.apiUrl}ElectricitySupplyContracts/${this._user.userCountryCode}/Get`, body, undefined, loaderConfig);
    }

    getElectricitySupplyContractEconomicConditions(elecSupplyContractId: string, body?: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<ElectricitySupplyContractEconomicCondition[]>> {
        if (String.isNullOrUndefinedOrWhitespace(elecSupplyContractId)) {
            return EMPTY;
        }
        
        if (!body) {
            body = new MVListingConfigs([],
            null,
            new ISPagination(999, 0));
        }

        return this._httpService.post(`${environment.apiUrl}ElectricitySupplyContracts/${this._user.userCountryCode}/${elecSupplyContractId}/EconomicConditions/Get`, body, undefined, loaderConfig);
    }

    createContractEnergySupplyProposal(contractId: string, body: ICreateContractEnergySupplyProposal, loaderConfig: LoaderConfig | boolean = true): Observable<ServerResponse<any>> {
        return this._httpService.post(`${environment.apiUrl}Contracts/${this._user.userCountryCode}/${contractId}/EnergySupplyProposal`, body, undefined, loaderConfig);
    }

    associateElectricitySupplyToContractEnergySupplyProposal(body: ICreateContractElectricitySupplyProposal, contractEnergySupplyProposalId: string, loaderConfig: LoaderConfig | boolean = true): Observable<any> {
        return this._httpService.post(`${environment.apiUrl}ContractEnergySupplyProposals/${this._user.userCountryCode}/${contractEnergySupplyProposalId}/ElectricitySupplyProposal`, body, undefined, loaderConfig);
    }
}